import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const BRAND = {
  primary: [47, 61, 142],     // #2F3D8E
  secondary: [93, 165, 216],  // #5DA5D8
  accent: [76, 140, 59],      // #4C8C3B
  dark: [26, 36, 86],
};

const FOOTER_TEXT_LINE_1 = 'Analytica, Avenida Naciones Unidas y Núñez de Vela, Edificio Metropolitan 3er piso';
const FOOTER_TEXT_LINE_2 = 'www.analytica-ec.com  -  fono: +593 991 466 821  -  Quito, Ecuador';

const sanitize = (s) => (s == null ? '' : String(s).replace(/[\u0000-\u001F\u007F-\u009F]/g, ''));

const fmtMoney = (n) =>
  '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  return `${d.getDate()} de ${meses[d.getMonth()]} de ${d.getFullYear()}`;
};

const drawHeader = (doc, proposalNumber) => {
  const w = doc.internal.pageSize.getWidth();
  // Top color band
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, w, 14, 'F');
  doc.setFillColor(...BRAND.secondary);
  doc.rect(0, 14, w, 1.5, 'F');

  // Logo (text-based, formal)
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('ANALYTICA', 12, 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Soluciones de Software & Analitica', 12, 12.5);

  // Right: proposal number
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(proposalNumber || '', w - 12, 9, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Propuesta Comercial', w - 12, 12.5, { align: 'right' });
};

const drawFooter = (doc) => {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  // Top line
  doc.setDrawColor(...BRAND.primary);
  doc.setLineWidth(0.4);
  doc.line(12, h - 16, w - 12, h - 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(80, 80, 80);
  doc.text(FOOTER_TEXT_LINE_1, w / 2, h - 11, { align: 'center' });
  doc.text(FOOTER_TEXT_LINE_2, w / 2, h - 7.5, { align: 'center' });

  // Page number bottom-right
  const totalPages = doc.internal.getNumberOfPages();
  const cur = doc.internal.getCurrentPageInfo().pageNumber;
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text(`Pagina ${cur} de ${totalPages}`, w - 12, h - 4, { align: 'right' });
};

const sectionTitle = (doc, text, y) => {
  doc.setFillColor(...BRAND.primary);
  doc.rect(12, y - 5, 4, 6, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...BRAND.primary);
  doc.text(sanitize(text), 19, y);
  doc.setDrawColor(...BRAND.secondary);
  doc.setLineWidth(0.3);
  doc.line(19, y + 1.5, 70, y + 1.5);
  return y + 7;
};

const ensureSpace = (doc, y, needed = 25) => {
  const h = doc.internal.pageSize.getHeight();
  if (y + needed > h - 22) {
    drawFooter(doc);
    doc.addPage();
    drawHeader(doc, doc._propNumber || '');
    return 26;
  }
  return y;
};

export async function generateProposalPdf(proposal) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const w = doc.internal.pageSize.getWidth();
  doc._propNumber = proposal.proposal_number;

  drawHeader(doc, proposal.proposal_number);
  let y = 26;

  // ===== Cover info =====
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...BRAND.dark);
  doc.text('Propuesta de Solucion de Software', 12, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(`Fecha: ${fmtDate(proposal.proposal_date)}`, 12, y + 13);
  doc.text(`Numero: ${proposal.proposal_number || ''}`, 12, y + 18);
  y += 30;

  // Client block
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(12, y, w - 24, 28, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.primary);
  doc.text('Dirigido a:', 16, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  doc.text(sanitize(proposal.client_company_name || ''), 16, y + 12);
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(
    `${proposal.client_representative_title || ''} ${sanitize(proposal.client_representative_name || '')}`.trim(),
    16, y + 17,
  );
  if (proposal.client_email) doc.text(`${proposal.client_email}`, 16, y + 22);
  if (proposal.client_phone) doc.text(`Tel: ${sanitize(proposal.client_phone)}`, 90, y + 22);
  if (proposal.client_city) doc.text(`${sanitize(proposal.client_city)}, ${sanitize(proposal.client_country || 'Ecuador')}`, w - 16, y + 12, { align: 'right' });
  y += 36;

  // ===== Introduccion =====
  y = sectionTitle(doc, 'Introduccion', y);
  const activity = sanitize(proposal.client_activity || 'su operacion');
  const intro =
    'Gracias por el espacio brindado en nuestra reunion y por compartirnos el contexto de su ' +
    'operacion. Tal como conversamos, hemos preparado una propuesta de desarrollo de software ' +
    'totalmente alineada a las necesidades de su operacion (' + activity + ').';
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  const introLines = doc.splitTextToSize(intro, w - 24);
  doc.text(introLines, 12, y);
  y += introLines.length * 5 + 5;

  // ===== 1. Objetivo de la solucion =====
  y = ensureSpace(doc, y, 30);
  y = sectionTitle(doc, '1. Objetivo de la Solucion', y);
  const objLines = doc.splitTextToSize(sanitize(proposal.objective || ''), w - 24);
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  doc.text(objLines, 12, y);
  y += objLines.length * 5 + 5;

  // ===== 2. Alcance general =====
  y = ensureSpace(doc, y, 30);
  y = sectionTitle(doc, '2. Alcance General del Desarrollo', y);
  const scope = (proposal.scope_items || []).filter(s => s && String(s).trim());
  if (scope.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('(sin items definidos)', 14, y);
    y += 8;
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    scope.forEach(s => {
      const lines = doc.splitTextToSize('•  ' + sanitize(s), w - 26);
      y = ensureSpace(doc, y, lines.length * 5 + 2);
      doc.text(lines, 14, y);
      y += lines.length * 5 + 1.5;
    });
    y += 3;
  }

  // ===== 3. Plan de Ejecucion =====
  y = ensureSpace(doc, y, 50);
  y = sectionTitle(doc, '3. Plan de Ejecucion', y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  doc.text(`Duracion total: ${proposal.total_weeks} semanas`, 12, y);
  y += 6;
  const acts = proposal.schedule_activities || [];
  if (acts.length) {
    const totalW = proposal.total_weeks || 8;
    const head = ['Actividad', ...Array.from({ length: totalW }).map((_, i) => `S${i + 1}`)];
    const body = acts.map(a => {
      const row = [sanitize((a.activity || '').substring(0, 60))];
      for (let i = 1; i <= totalW; i++) {
        row.push((a.weeks || []).includes(i) ? '■' : '');
      }
      return row;
    });
    autoTable(doc, {
      head: [head],
      body,
      startY: y,
      theme: 'grid',
      headStyles: { fillColor: BRAND.primary, textColor: 255, fontSize: 8, halign: 'center' },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 65, fontStyle: 'normal' } },
      styles: { halign: 'center', cellPadding: 1.4 },
      margin: { left: 12, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 5;
  } else {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('(sin actividades definidas en el cronograma)', 14, y);
    y += 8;
  }

  // ===== 4. Beneficios clave =====
  y = ensureSpace(doc, y, 30);
  y = sectionTitle(doc, '4. Beneficios Clave de Operacion, Garantia y Acompañamiento', y);
  const benefits = (proposal.benefits || []).filter(b => b && String(b).trim());
  if (benefits.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('(sin beneficios definidos)', 14, y);
    y += 8;
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    benefits.forEach(b => {
      const lines = doc.splitTextToSize('•  ' + sanitize(b), w - 26);
      y = ensureSpace(doc, y, lines.length * 5 + 2);
      doc.text(lines, 14, y);
      y += lines.length * 5 + 1.5;
    });
    y += 3;
  }

  // ===== 5. Propuesta financiera =====
  y = ensureSpace(doc, y, 60);
  y = sectionTitle(doc, '5. Propuesta Financiera', y);
  const phases = proposal.payment_phases || [];
  if (phases.length) {
    autoTable(doc, {
      head: [['Fase', 'Descripcion', 'Semana', 'Monto (USD)']],
      body: phases.map((p, i) => [
        i + 1,
        sanitize(p.description || ''),
        p.week || '-',
        fmtMoney(p.amount || 0),
      ]),
      foot: [['', '', 'Total', fmtMoney(proposal.total_price || phases.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0))]],
      startY: y,
      theme: 'striped',
      headStyles: { fillColor: BRAND.primary, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      footStyles: { fillColor: BRAND.dark, textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { halign: 'center', cellWidth: 16 },
        2: { halign: 'center', cellWidth: 22 },
        3: { halign: 'right', cellWidth: 32 },
      },
      margin: { left: 12, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 4;
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  const finFootnote = doc.splitTextToSize(
    'Los valores se expresan en dolares de los Estados Unidos de America (USD) y no incluyen IVA, ' +
    'salvo que se indique lo contrario en el contrato definitivo.',
    w - 24,
  );
  doc.text(finFootnote, 12, y);
  y += finFootnote.length * 4 + 4;

  // ===== Firma =====
  y = ensureSpace(doc, y, 70);
  y = sectionTitle(doc, 'Atentamente,', y);

  // Try to embed the actual signature image
  try {
    const sigUrl = '/assets/firma_electronica_amaya.png';
    const resp = await fetch(sigUrl);
    if (resp.ok) {
      const blob = await resp.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
      // Render signature: width ~55mm, preserve aspect ratio
      doc.addImage(dataUrl, 'PNG', 12, y, 55, 22);
    }
  } catch (e) {
    // If image fails, just continue without it
  }

  // Signature line
  doc.setDrawColor(60, 60, 60);
  doc.setLineWidth(0.4);
  doc.line(12, y + 24, 90, y + 24);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.dark);
  doc.text('Alejandro Amaya', 12, y + 29);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text('CEO ANALYTICA', 12, y + 34);
  doc.text('Correo: gerencia@analytica-ec.com', 12, y + 38.5);
  doc.text('Telefono / WhatsApp: +593 991 466 821', 12, y + 43);

  // Final footer on all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  doc.save(`Propuesta_${proposal.proposal_number || 'Analytica'}.pdf`);
}
